import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import React from "react";
import { AuthProvider } from "./context/AuthContext";
import Login from "./auth/login-wrapper";
import PublicRoutes from "./routes/PublicRoutes";
import ProtectedRoutes from "./routes/ProtectedRoutes";
import Home from "./pages/Home";
import { UserRoles, UserStatus } from "./types/user.types";
import Unautorized from "./pages/Unauthorized";
import CreateTeamForm from "./pages/create-team/create-team-form";
import { ToastProvider } from "./components/toast";
import Institutions from "./pages/institutions/institutions";
import { CompetitionsRoleRouter } from "./pages/competitions/competitions-role-router";
import { CompetitionsEdit } from "./pages/competitions/competitions-edit";
import { SidebarProvider } from "./context/SidebarContext";
import { SignUpWrapper } from "./auth/signup-wrapper";
import { ActivateAccount } from "./pages/activate-account/activate-account-wrapper";
import { InstitutionsProvider } from "./context/InstitutionsContext";
import { InstitutionsAdd } from "./pages/institutions/institutions-add";
import { InstitutionsEdit } from "./pages/institutions/institutions-edit";
import { MyTeam } from "./pages/my-team/pages/my-team";
import { LeaveTeam } from "./pages/my-team/leave-team/leave-team";
import { SuccessPayment } from "./pages/competitions/payments/success-payment";
import { MyBoat } from "./pages/my-team/pages/my-boat";
import { MyMembers } from "./pages/my-team/pages/my-members";
import { TeamProviderWrapper } from "./components/team-provider-wrapper";
import { BoatsTableAdmins } from "./pages/boats/boats-admins";
import { PaymentsTableAdmins } from "./pages/payments/payments-admins";
import { UsersCRUD } from "./pages/users/users-admins";
import { UsersAdd } from "./pages/users/users-add";
import { UsersProviderWrapper } from "./components/users-provider-wrapper";
import { CompetitionView } from "./pages/competitions/view/competition-view";
import { TeamAdd } from "./pages/competitions/view/team-add";
import { RequestInstitutionForm } from "./components/request-institution-form";
import { HelpDeskCRUD } from "./pages/help-desk/help-desk-admins";
import { HelpDeskTicketsProviderWrapper } from "./components/help-desk-tickets-provider-wrapper";
import { HelpDeskView } from "./pages/help-desk/help-desk-view";
import { CompetitionsProviderWrapper } from "./components/competitions-provider-wrapper";
import { TeamProvider } from "./context/TeamContext";
import { TeamsCRUD } from "./pages/teams/teams.admins";
import { TeamMembersAdmins } from "./pages/team-members/team-members-admins";
import { TeamView } from "./pages/teams/team-view";
import { Dashboard } from "./pages/dashboard-refactor/dashboard";
import { UsersHelpPage } from "./pages/help-desk/hdt-users";
import { DeleteTeam } from "./pages/my-team/delete-team/delete-team";
import { CompetitionsAdd } from "./pages/competitions/competitions-add";
import { UsersView } from "./pages/users/users-view/users-view";
import { UserNotificationsProvider } from "./context/UserNotificationsContext";
import { NotificationsPage } from "./pages/notifications/notifications";
import { TeamsNotificationsPage } from "./pages/notifications/teams-notifications";
import ForgotCredentials from "./pages/forgot-credentials/forgot-credentials-wrapper";
import { CredentialsRedirect } from "./pages/credentials-redirect";
import { PriceEditForm } from "./pages/competitions/view/price-edit-form";
import { PriceAddForm } from "./pages/competitions/view/price-add-form";
import { PasswordRestore } from "./components/reset-password-page";
import { PaymentDetailsView } from "./pages/payments/payments-view";
import { TransferPaymentView } from "./pages/competitions/payments/transfer-payment-view";
import { PendingTransfersTable } from "./pages/payments/pending-transfers";
import { TeamEdit } from "./pages/competitions/view/team-edit";
import { BoatCreate } from "./pages/competitions/view/boat-add";
import { BoatEdit } from "./pages/competitions/view/boat-edit";
import { TeamMemberEditPage } from "./pages/teams/tm/team-member-edit";
import { TeamMemberAddPage } from "./pages/teams/tm/team-member-add";
import { CompetitionResultsAdmin } from "./pages/competitions/view/results/competition-results";
import { CompetitionResultsUsers } from "./pages/competitions/competitions-results-users";

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SidebarProvider>
        <ToastProvider>
          <Router>
            <Routes>
              <Route path="/" element={<PublicRoutes element={<Login />} />} />
              <Route
                path="/signup"
                element={<PublicRoutes element={<SignUpWrapper />} />}
              />
              <Route
                path="/forgot-credentials"
                element={<PublicRoutes element={<ForgotCredentials />} />}
              />

              <Route element={<CompetitionsProviderWrapper />}>
                <Route
                  path="/dashboard"
                  element={
                    <UserNotificationsProvider>
                      <ProtectedRoutes element={<Dashboard />} />
                    </UserNotificationsProvider>
                  }
                >
                  <Route index element={<Home />} />
                  <Route
                    path="home"
                    element={<ProtectedRoutes element={<Home />} />}
                  />
                  <Route
                    path="activate-account"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.PRIMARY_USER,
                          UserRoles.INVITED_USER,
                        ]}
                        requiredStatus={UserStatus.REGISTERED}
                        element={<ActivateAccount />}
                      />
                    }
                  />
                  <Route
                    path="create-team"
                    element={
                      <InstitutionsProvider>
                        <ProtectedRoutes
                          allowedRoles={[UserRoles.PRIMARY_USER]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<CreateTeamForm />}
                        />
                      </InstitutionsProvider>
                    }
                  />
                  <Route
                    path="leave-team"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.INVITED_USER]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<LeaveTeam />}
                      />
                    }
                  />

                  <Route
                    path="delete-team"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.PRIMARY_USER]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<DeleteTeam />}
                      />
                    }
                  />
                  <Route element={<TeamProviderWrapper />}>
                    <Route
                      path="my-team"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.PRIMARY_USER,
                            UserRoles.INVITED_USER,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<MyTeam />}
                        />
                      }
                    />
                    <Route
                      path="my-boat"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.PRIMARY_USER,
                            UserRoles.INVITED_USER,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<MyBoat />}
                        />
                      }
                    />
                    <Route
                      path="my-members"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.PRIMARY_USER,
                            UserRoles.INVITED_USER,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<MyMembers />}
                        />
                      }
                    />
                  </Route>

                  <Route path="competitions/*">
                    <Route
                      index
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.PRIMARY_USER,
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.INVITED_USER,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<CompetitionsRoleRouter />}
                        ></ProtectedRoutes>
                      }
                    />
                    <Route
                      path="new"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<CompetitionsAdd />}
                        ></ProtectedRoutes>
                      }
                    />
                    <Route
                      path="payment/success/"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[UserRoles.PRIMARY_USER]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<SuccessPayment />}
                        ></ProtectedRoutes>
                      }
                    />
                    <Route
                      path="edit/:id"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<CompetitionsEdit />}
                        ></ProtectedRoutes>
                      }
                    />
                    <Route
                      path="view/:id"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<CompetitionView />}
                        ></ProtectedRoutes>
                      }
                    />
                    <Route
                      path=":id/results"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<CompetitionResultsAdmin />}
                        ></ProtectedRoutes>
                      }
                    />
                  </Route>

                  <Route
                    path="institutions/*"
                    element={
                      <InstitutionsProvider>
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<Institutions />}
                        />
                      </InstitutionsProvider>
                    }
                  >
                    <Route
                      path="new"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<InstitutionsAdd />}
                        />
                      }
                    />
                    <Route
                      path="edit/:id"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<InstitutionsEdit />}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="boats/*"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.CLIENT_ADMIN,
                          UserRoles.SYSTEM_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={
                          <Routes>
                            <Route index element={<BoatsTableAdmins />} />
                            <Route path="create" element={<BoatCreate />} />
                            <Route path="edit/:id" element={<BoatEdit />} />
                          </Routes>
                        }
                      />
                    }
                  />
                  <Route
                    path="payments"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.CLIENT_ADMIN,
                          UserRoles.SYSTEM_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<Outlet />}
                      />
                    }
                  >
                    <Route index element={<PaymentsTableAdmins />} />
                    <Route path="view/:id" element={<PaymentDetailsView />} />
                  </Route>

                   <Route
                    path="results"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.PRIMARY_USER,
                          UserRoles.INVITED_USER,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<Outlet />}
                      />
                    }
                  >
                    <Route index element={<CompetitionResultsUsers />} />
                  </Route>

                  <Route
                    path="pending-transfers"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.CLIENT_ADMIN,
                          UserRoles.SYSTEM_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<Outlet />}
                      />
                    }
                  >
                    <Route index element={<PendingTransfersTable />} />
                    <Route path="view/:id" element={<PaymentDetailsView />} />
                  </Route>

                  <Route path="users/*" element={<UsersProviderWrapper />}>
                    <Route
                      index
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<UsersCRUD />}
                        />
                      }
                    />
                    <Route
                      path="new"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<UsersAdd />}
                        />
                      }
                    />
                    <Route
                      path="view/:id"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<UsersView />}
                        />
                      }
                    />
                  </Route>
                  <Route path="teams/*">
                    <Route
                      index
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={
                            <TeamProvider>
                              <TeamsCRUD />
                            </TeamProvider>
                          }
                        />
                      }
                    />
                    <Route
                      path="view/:id"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.SYSTEM_ADMIN,
                            UserRoles.CLIENT_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<TeamView />}
                        />
                      }
                    />
                    <Route
                      path="edit/:id"
                      element={
                        <InstitutionsProvider>
                          <ProtectedRoutes
                            allowedRoles={[
                              UserRoles.SYSTEM_ADMIN,
                              UserRoles.CLIENT_ADMIN,
                            ]}
                            requiredStatus={UserStatus.ACTIVE}
                            element={<TeamEdit />}
                          />
                        </InstitutionsProvider>
                      }
                    />
                    <Route
                      path="new"
                      element={
                        <InstitutionsProvider>
                          <ProtectedRoutes
                            allowedRoles={[
                              UserRoles.CLIENT_ADMIN,
                              UserRoles.SYSTEM_ADMIN,
                            ]}
                            requiredStatus={UserStatus.ACTIVE}
                            element={<TeamAdd />}
                          />
                        </InstitutionsProvider>
                      }
                    />
                  </Route>
                  <Route path="team-members/*">
                    <Route
                      index
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<TeamMembersAdmins />}
                        />
                      }
                    />
                    <Route
                      path="add"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<TeamMemberAddPage />}
                        />
                      }
                    />
                    <Route
                      path="edit/:userId"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<TeamMemberEditPage />}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="request-institution"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.PRIMARY_USER]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<RequestInstitutionForm />}
                      />
                    }
                  />
                  <Route
                    path="transfer/:id"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.PRIMARY_USER]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<TransferPaymentView />}
                      />
                    }
                  />
                  <Route
                    path="help-desk"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.CLIENT_ADMIN,
                          UserRoles.SYSTEM_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<HelpDeskCRUD />}
                      />
                    }
                  />
                  <Route
                    path="me/reset-password"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.SYSTEM_ADMIN,
                          UserRoles.CLIENT_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<PasswordRestore />}
                      />
                    }
                  />
                  <Route
                    path="help-desk/*"
                    element={<HelpDeskTicketsProviderWrapper />}
                  >
                    <Route
                      index
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<HelpDeskCRUD />}
                        />
                      }
                    />
                    <Route
                      path="view/:id"
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.CLIENT_ADMIN,
                            UserRoles.SYSTEM_ADMIN,
                          ]}
                          requiredStatus={UserStatus.ACTIVE}
                          element={<HelpDeskView />}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="help/*"
                    element={<HelpDeskTicketsProviderWrapper />}
                  >
                    <Route
                      index
                      element={
                        <ProtectedRoutes
                          allowedRoles={[
                            UserRoles.PRIMARY_USER,
                            UserRoles.INVITED_USER,
                          ]}
                          element={<UsersHelpPage />}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="notifications"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.SYSTEM_ADMIN]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<NotificationsPage />}
                      />
                    }
                  />
                  <Route
                    path="notifications/teams"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.CLIENT_ADMIN,
                          UserRoles.SYSTEM_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<TeamsNotificationsPage />}
                      />
                    }
                  />
                  <Route
                    path="new-credentials"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[
                          UserRoles.SYSTEM_ADMIN,
                          UserRoles.CLIENT_ADMIN,
                        ]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<CredentialsRedirect />}
                      />
                    }
                  />

                  <Route
                    path="price/edit/:id"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.SYSTEM_ADMIN]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<PriceEditForm />}
                      />
                    }
                  />
                  <Route
                    path="price/new"
                    element={
                      <ProtectedRoutes
                        allowedRoles={[UserRoles.SYSTEM_ADMIN]}
                        requiredStatus={UserStatus.ACTIVE}
                        element={<PriceAddForm />}
                      />
                    }
                  />
                </Route>
              </Route>

              <Route path="/home" element={<Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={<Navigate to="/dashboard" />} />
              <Route path="/unauthorized" element={<Unautorized />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </ToastProvider>
      </SidebarProvider>
    </AuthProvider>
  );
};

export default App;
